import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, inject, signal } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  TiktokAdGroupLanguageOption,
  TiktokAdGroupLocationOption,
} from '@/app/common/general-types/tiktok-spark-ads.dto';
import { CreateSparkCampaign } from '../../../../../../service/create-spark-campaign/create-spark-campaign';
import { LocIndeterminateDirective } from './loc-indeterminate.directive';

function filterRegionTree(
  nodes: TiktokAdGroupLocationOption[],
  query: string,
): TiktokAdGroupLocationOption[] {
  const q = query.trim().toLowerCase();
  if (!q) {
    return nodes;
  }
  const out: TiktokAdGroupLocationOption[] = [];
  for (const n of nodes) {
    const display = (n.name ?? '').trim() || String(n.location_id ?? '');
    const nameMatch = display.toLowerCase().includes(q);
    const children = n.sub_regions ?? [];
    const filteredChildren = filterRegionTree(children, query);
    if (nameMatch || filteredChildren.length > 0) {
      out.push({
        ...n,
        sub_regions: filteredChildren.length > 0 ? filteredChildren : children.length ? children : undefined,
      });
    }
  }
  return out;
}

/** Exclude sub-national rows from the root list when TikTok mixes levels in `region_info`. */
function isSubNationalRootLevel(level: string | undefined): boolean {
  if (level === undefined || level.trim() === '') {
    return false;
  }
  const l = level.trim().toUpperCase();
  const exact = new Set([
    'PROVINCE',
    'STATE',
    'CITY',
    'DMA',
    'DISTRICT',
    'COUNTY',
    'METRO',
    'METROPOLITAN',
  ]);
  if (exact.has(l)) {
    return true;
  }
  return (
    l.includes('PROVINCE') ||
    l.includes('DMA') ||
    (l.includes('STATE') && !l.includes('COUNTRY')) ||
    (l.includes('CITY') && !l.includes('COUNTRY')) ||
    l.includes('DISTRICT')
  );
}

export type GenderTargetingValue = 'GENDER_FEMALE' | 'GENDER_MALE' | 'GENDER_UNLIMITED';

export type AgeRangeValue =
  | 'AGE_18_24'
  | 'AGE_25_34'
  | 'AGE_35_44'
  | 'AGE_45_54'
  | 'AGE_55_100';

/** Direct children of a node, grouped by TikTok `level` for lazy accordion sections. */
export interface RegionLevelGroup {
  levelKey: string;
  title: string;
  items: TiktokAdGroupLocationOption[];
}

const LEVEL_GROUP_ORDER: string[] = [
  'PROVINCE',
  'STATE',
  'REGION',
  'DMA',
  'METROPOLITAN',
  'METRO',
  'CITY',
  'DISTRICT',
  'COUNTY',
  'OTHER',
];

function levelGroupSortKey(levelKey: string): number {
  const u = levelKey.toUpperCase();
  const idx = LEVEL_GROUP_ORDER.indexOf(u);
  return idx === -1 ? LEVEL_GROUP_ORDER.length + 1 : idx;
}

const SEARCH_WALK_NODE_BUDGET = 40000;

/** Every `location_id` in a node's subtree (including the node). */
function collectSubtreeLocationIds(node: TiktokAdGroupLocationOption): string[] {
  const out: string[] = [node.location_id];
  for (const c of node.sub_regions ?? []) {
    out.push(...collectSubtreeLocationIds(c));
  }
  return out;
}

function isUnitedKingdomRegionLabel(name: string | undefined): boolean {
  const label = (name ?? '').trim().toLowerCase();
  if (!label) {
    return false;
  }
  return (
    label === 'united kingdom' ||
    label === 'great britain' ||
    label === 'uk' ||
    label.includes('united kingdom')
  );
}

/** First tree node whose display name is the UK (country or nested), for subtree id matching. */
function findUnitedKingdomRegionRoot(
  nodes: TiktokAdGroupLocationOption[],
): TiktokAdGroupLocationOption | undefined {
  for (const n of nodes) {
    if (isUnitedKingdomRegionLabel(n.name)) {
      return n;
    }
    const found = findUnitedKingdomRegionRoot(n.sub_regions ?? []);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/** Map each node in `countryRoot`'s subtree to that country's root id. */
function assignIdToCountryRoot(countryRoot: TiktokAdGroupLocationOption, map: Map<string, string>): void {
  const walk = (n: TiktokAdGroupLocationOption) => {
    map.set(n.location_id, countryRoot.location_id);
    for (const c of n.sub_regions ?? []) {
      walk(c);
    }
  };
  walk(countryRoot);
}

/** One removable chip in the “Selected regions” summary (whole country vs one sub-region). */
interface LocationSummaryChip {
  readonly displayLabel: string;
  /** All `location_ids` to remove when this chip is cleared */
  readonly removeIds: readonly string[];
}

/** Country heading + chips for the summary panel. */
interface LocationSummaryCountryGroup {
  readonly countryHeading: string;
  readonly chips: readonly LocationSummaryChip[];
}

function collectSearchMatches(nodes: TiktokAdGroupLocationOption[], q: string, cap: number): TiktokAdGroupLocationOption[] {
  const ql = q.toLowerCase();
  const out: TiktokAdGroupLocationOption[] = [];
  let visited = 0;
  const walk = (list: TiktokAdGroupLocationOption[]) => {
    for (const n of list) {
      if (out.length >= cap || visited >= SEARCH_WALK_NODE_BUDGET) {
        return;
      }
      visited += 1;
      const display = ((n.name ?? '').trim() || String(n.location_id ?? '')).toLowerCase();
      if (display.includes(ql)) {
        out.push(n);
      }
      if (n.sub_regions?.length) {
        walk(n.sub_regions);
      }
    }
  };
  walk(nodes);
  return out;
}

@Component({
  selector: 'targeting-demographic',
  imports: [ReactiveFormsModule, NgTemplateOutlet, LocIndeterminateDirective],
  templateUrl: './demographic.html',
  styleUrl: './demographic.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class Demographic {
  expanded = signal<boolean>(false);

  public createSparkCampaignService = inject(CreateSparkCampaign);

  readonly locationSearchQuery = signal('');

  readonly languageSearchQuery = signal('');

  readonly filteredLanguageOptions = computed(() => {
    const opts = this.createSparkCampaignService.adGroupLanguageOptions() ?? [];
    const q = this.languageSearchQuery().trim().toLowerCase();
    if (!q) {
      return opts;
    }
    return opts.filter((o) => {
      const name = (o.name ?? '').toLowerCase();
      return name.includes(q) || o.code.toLowerCase().includes(q);
    });
  });

  readonly selectedLanguageCodesSorted = computed(() => {
    const svc = this.createSparkCampaignService;
    const labelMap = svc.languageLabelsByCode();
    return [...svc.languages()].sort((a, b) => {
      const la = labelMap.get(a) ?? a;
      const lb = labelMap.get(b) ?? b;
      return la.localeCompare(lb);
    });
  });

  /** Expanded location rows (country / province / …) that have children. */
  readonly expandedLocationIds = signal<ReadonlySet<string>>(new Set<string>());

  /** `parentLocationId|levelKey` — which level-type accordion is open under a parent. */
  readonly expandedLevelGroupIds = signal<ReadonlySet<string>>(new Set<string>());

  readonly selectedLocationsPanelOpen = signal<boolean>(true);

  /** Root countries only when not searching (search uses `searchFlatMatches` to avoid cloning the whole tree). */
  readonly filteredRegionTree = computed(() => {
    const raw = this.createSparkCampaignService.adGroupLocations() ?? [];
    if (this.locationSearchQuery().trim()) {
      return [];
    }
    return filterRegionTree(raw, '');
  });

  /** Flat matches when searching — avoids expanding the full tree (major perf win). */
  readonly searchFlatMatches = computed(() => {
    const q = this.locationSearchQuery().trim();
    if (!q) {
      return [];
    }
    return collectSearchMatches(this.createSparkCampaignService.adGroupLocations() ?? [], q, 300);
  });

  readonly isLocationSearchActive = computed(() => this.locationSearchQuery().trim().length > 0);

  /**
   * Root rows for browse mode: prefer countries, not provinces/states/cities mixed at root.
   * Does not fall back to “all roots” when every root looks sub-national (that was re-rendering huge province lists).
   */
  readonly rootCountriesForUi = computed(() => {
    const roots = this.filteredRegionTree();
    const nonSubNational = roots.filter((n) => !isSubNationalRootLevel(n.level));
    if (nonSubNational.length > 0) {
      return nonSubNational;
    }
    const countryTagged = roots.filter((n) => (n.level ?? '').trim().toUpperCase() === 'COUNTRY');
    if (countryTagged.length > 0) {
      return countryTagged;
    }
    const withChildren = roots.filter((n) => (n.sub_regions?.length ?? 0) > 0);
    if (withChildren.length > 0) {
      return withChildren;
    }
    // Do not return raw roots here: a flat province list at root would still freeze the UI (detail/search paths).
    return [];
  });

  /** One country’s sub-tree rendered below the virtual list (avoids variable-height rows in CDK viewport). */
  readonly detailCountryNode = signal<TiktokAdGroupLocationOption | null>(null);

  readonly hfssControl = new FormControl<boolean>(false, { nonNullable: true });

  /** HFSS applies to UK ads only — show when any selected location is under the United Kingdom in TikTok’s tree. */
  readonly showHfssSection = computed(() => {
    const roots = this.createSparkCampaignService.adGroupLocations() ?? [];
    const ukRoot = findUnitedKingdomRegionRoot(roots);
    if (!ukRoot) {
      return false;
    }
    const ukIds = new Set(collectSubtreeLocationIds(ukRoot));
    return this.createSparkCampaignService.location_ids().some((id) => ukIds.has(id));
  });

  readonly ageOptions: { value: AgeRangeValue; label: string }[] = [
    { value: 'AGE_18_24', label: '18–24' },
    { value: 'AGE_25_34', label: '25–34' },
    { value: 'AGE_35_44', label: '35–44' },
    { value: 'AGE_45_54', label: '45–54' },
    { value: 'AGE_55_100', label: '55+' },
  ];

  readonly genderOptions: { value: GenderTargetingValue; label: string }[] = [
    { value: 'GENDER_FEMALE', label: 'Female' },
    { value: 'GENDER_MALE', label: 'Male' },
    { value: 'GENDER_UNLIMITED', label: 'All' },
  ];

  constructor() {
    const svc = this.createSparkCampaignService;
    this.hfssControl.setValue(svc.is_hfss(), { emitEvent: false });
    this.hfssControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((checked) => {
      svc.is_hfss.set(checked);
    });

    effect(() => {
      svc.adGroupLocations();
      this.detailCountryNode.set(null);
      this.expandedLevelGroupIds.set(new Set());
      this.expandedLocationIds.set(new Set());
    });

    effect(() => {
      if (!this.showHfssSection()) {
        svc.is_hfss.set(false);
        this.hfssControl.setValue(false, { emitEvent: false });
      }
    });
  }

  toggleAge(value: AgeRangeValue): void {
    const svc = this.createSparkCampaignService;
    const current = svc.age();
    if (current.includes(value)) {
      if (current.length <= 1) {
        return;
      }
      svc.age.set(current.filter((v) => v !== value));
      return;
    }
    svc.age.set([...current, value]);
  }

  isAgeSelected(value: AgeRangeValue): boolean {
    return this.createSparkCampaignService.age().includes(value);
  }

  ageToggleClass(value: AgeRangeValue): string {
    const selected = this.isAgeSelected(value);
    const base =
      'flex w-full min-w-0 items-center justify-center rounded-md px-2 py-2 text-xs font-semibold sm:text-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary';
    if (selected) {
      return `${base} bg-primary text-white shadow-sm inset-ring-1 inset-ring-primary dark:bg-primary dark:inset-ring-primary`;
    }
    return `${base} bg-white text-gray-900 inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-gray-700 dark:hover:bg-white/20`;
  }

  ageSummaryLabel(): string {
    const selected = this.createSparkCampaignService.age();
    const labels = this.ageOptions.filter((o) => selected.includes(o.value)).map((o) => o.label);
    if (labels.length === 0) {
      return 'None';
    }
    if (labels.length === this.ageOptions.length) {
      return '18–24, 25–34, 35–44, 45–54, 55+';
    }
    return labels.join(', ');
  }

  selectGender(value: GenderTargetingValue): void {
    this.createSparkCampaignService.gender.set(value);
  }

  isGenderSelected(value: GenderTargetingValue): boolean {
    return this.createSparkCampaignService.gender() === value;
  }

  genderSegmentClass(value: GenderTargetingValue, index: number): string {
    const selected = this.isGenderSelected(value);
    const rounded =
      index === 0 ? 'rounded-l-md' : index === this.genderOptions.length - 1 ? 'rounded-r-md' : '';
    const overlap = index > 0 ? '-ml-px' : '';
    const base =
      `relative flex min-w-0 flex-1 basis-0 items-center justify-center px-3 py-2 text-sm font-semibold focus:z-10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${overlap} ${rounded}`;

    if (selected) {
      return `${base} z-10 bg-primary text-white shadow-sm inset-ring-1 inset-ring-primary dark:bg-primary dark:inset-ring-primary`;
    }
    return `${base} bg-white text-gray-900 inset-ring-1 inset-ring-gray-300 hover:bg-gray-50 dark:bg-white/10 dark:text-white dark:inset-ring-gray-700 dark:hover:bg-white/20`;
  }

  genderSummaryLabel(): string {
    switch (this.createSparkCampaignService.gender()) {
      case 'GENDER_FEMALE':
        return 'Female';
      case 'GENDER_MALE':
        return 'Male';
      default:
        return 'All';
    }
  }

  languagesSummaryLabel(): string {
    const codes = this.createSparkCampaignService.languages();
    if (codes.length === 0) {
      return 'All';
    }
    const labelMap = this.createSparkCampaignService.languageLabelsByCode();
    const labels = codes
      .map((c) => labelMap.get(c) ?? c)
      .sort((a, b) => a.localeCompare(b));
    return labels.join(', ');
  }

  onLanguageSearchInput(ev: Event): void {
    const el = ev.target as HTMLInputElement;
    this.languageSearchQuery.set(el.value);
  }

  trackLanguageOption(_index: number, opt: TiktokAdGroupLanguageOption): string {
    return opt.code;
  }

  isLanguageSelected(code: string): boolean {
    return this.createSparkCampaignService.languages().includes(code);
  }

  onLanguageCheckboxChange(code: string, ev: Event): void {
    const el = ev.target as HTMLInputElement;
    const checked = el.checked;
    const svc = this.createSparkCampaignService;
    if (checked) {
      if (!svc.languages().includes(code)) {
        svc.languages.set([...svc.languages(), code]);
      }
    } else {
      svc.languages.set(svc.languages().filter((c) => c !== code));
    }
  }

  removeLanguageCode(code: string): void {
    const svc = this.createSparkCampaignService;
    svc.languages.set(svc.languages().filter((c) => c !== code));
  }

  clearAllLanguageSelections(): void {
    this.createSparkCampaignService.languages.set([]);
  }

  languageRowClass(code: string): string {
    const selected = this.isLanguageSelected(code);
    const base =
      'flex min-h-10 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-gray-900 transition-colors dark:text-gray-100';
    if (selected) {
      return `${base} bg-primary/10 dark:bg-primary/25`;
    }
    return `${base} hover:bg-gray-50 dark:hover:bg-white/5`;
  }

  /**
   * Groups selected `location_ids` under country headings. Whole country → one chip with the country name;
   * partial subtree → chips for selected provinces/cities/DMAs only (not the country row).
   */
  readonly selectedLocationSummaryGroups = computed((): LocationSummaryCountryGroup[] => {
    const svc = this.createSparkCampaignService;
    const roots = svc.adGroupLocations() ?? [];
    const ids = svc.location_ids();
    const labelMap = svc.locationLabelsById();
    if (ids.length === 0) {
      return [];
    }

    const selectedSet = new Set(ids);
    const idToCountryRoot = new Map<string, string>();
    const countryRootById = new Map<string, TiktokAdGroupLocationOption>();
    for (const root of roots) {
      countryRootById.set(root.location_id, root);
      assignIdToCountryRoot(root, idToCountryRoot);
    }

    const byCountry = new Map<string, Set<string>>();
    const orphans: string[] = [];
    for (const id of ids) {
      const countryId = idToCountryRoot.get(id);
      if (countryId === undefined) {
        orphans.push(id);
        continue;
      }
      let bucket = byCountry.get(countryId);
      if (!bucket) {
        bucket = new Set();
        byCountry.set(countryId, bucket);
      }
      bucket.add(id);
    }

    const groups: LocationSummaryCountryGroup[] = [];

    for (const [countryId, selectedInCountry] of byCountry) {
      const rootNode = countryRootById.get(countryId);
      if (!rootNode) {
        continue;
      }
      const subtreeIds = collectSubtreeLocationIds(rootNode);
      const isFull = subtreeIds.every((sid) => selectedSet.has(sid));
      const countryHeading =
        labelMap.get(countryId) ?? rootNode.name?.trim() ?? countryId;
      const rootId = rootNode.location_id;

      let chips: LocationSummaryChip[];
      if (isFull) {
        chips = [
          {
            displayLabel: countryHeading,
            removeIds: subtreeIds,
          },
        ];
      } else {
        const selectedList = [...selectedInCountry].sort((a, b) => {
          const la = labelMap.get(a) ?? a;
          const lb = labelMap.get(b) ?? b;
          return la.localeCompare(lb);
        });
        const withoutRoot = selectedList.filter((id) => id !== rootId);
        if (withoutRoot.length > 0) {
          chips = withoutRoot.map((id) => ({
            displayLabel: labelMap.get(id) ?? id,
            removeIds: [id],
          }));
        } else {
          chips = [
            {
              displayLabel: countryHeading,
              removeIds: [rootId],
            },
          ];
        }
      }

      groups.push({ countryHeading, chips });
    }

    if (orphans.length > 0) {
      const sorted = [...orphans].sort((a, b) => {
        const la = labelMap.get(a) ?? a;
        const lb = labelMap.get(b) ?? b;
        return la.localeCompare(lb);
      });
      groups.push({
        countryHeading: 'Other regions',
        chips: sorted.map((id) => ({
          displayLabel: labelMap.get(id) ?? id,
          removeIds: [id],
        })),
      });
    }

    groups.sort((a, b) => a.countryHeading.localeCompare(b.countryHeading));
    return groups;
  });

  locationSummaryLabel(): string {
    const groups = this.selectedLocationSummaryGroups();
    if (groups.length === 0) {
      return 'None';
    }
    const parts: string[] = [];
    for (const g of groups) {
      const chip0 = g.chips[0];
      if (g.chips.length === 1 && chip0 && chip0.displayLabel === g.countryHeading) {
        parts.push(g.countryHeading);
      } else {
        const names = g.chips.map((c) => c.displayLabel).join(', ');
        parts.push(`${g.countryHeading} (${names})`);
      }
    }
    return parts.join('; ');
  }

  selectedRegionsHeading(): string {
    return 'Selected regions';
  }

  trackSummaryChip(_index: number, chip: LocationSummaryChip): string {
    return `${chip.removeIds.join(',')}:${chip.displayLabel}`;
  }

  trackCountryGroup(_index: number, group: LocationSummaryCountryGroup): string {
    return group.countryHeading;
  }

  removeSummaryChip(chip: LocationSummaryChip): void {
    const remove = new Set(chip.removeIds);
    const svc = this.createSparkCampaignService;
    svc.location_ids.set(svc.location_ids().filter((id) => !remove.has(id)));
  }

  onLocationSearchInput(ev: Event): void {
    const el = ev.target as HTMLInputElement;
    this.locationSearchQuery.set(el.value);
  }

  nodeHasChildren(node: TiktokAdGroupLocationOption): boolean {
    return (node.sub_regions?.length ?? 0) > 0;
  }

  isNodeExpanded(node: TiktokAdGroupLocationOption): boolean {
    if (!this.nodeHasChildren(node)) {
      return false;
    }
    return this.expandedLocationIds().has(node.location_id);
  }

  toggleNodeExpanded(id: string): void {
    this.expandedLocationIds.update((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  /** Expand/collapse a country’s sub-regions inline under its row. */
  toggleCountryDetailPanel(country: TiktokAdGroupLocationOption): void {
    if (!this.nodeHasChildren(country)) {
      return;
    }
    this.detailCountryNode.update((cur) => {
      if (cur?.location_id === country.location_id) {
        this.collapseLevelGroupsForParent(country.location_id);
        return null;
      }
      if (cur) {
        this.collapseLevelGroupsForParent(cur.location_id);
      }
      this.expandAllLevelGroupsForParent(country);
      return country;
    });
  }

  private collapseLevelGroupsForParent(parentId: string): void {
    const prefix = `${parentId}|`;
    this.expandedLevelGroupIds.update((prev) => {
      const next = new Set<string>();
      for (const k of prev) {
        if (!k.startsWith(prefix)) {
          next.add(k);
        }
      }
      return next;
    });
  }

  private expandAllLevelGroupsForParent(parent: TiktokAdGroupLocationOption): void {
    const groups = this.groupChildrenByLevel(parent);
    this.expandedLevelGroupIds.update((prev) => {
      const next = new Set(prev);
      for (const g of groups) {
        next.add(this.levelGroupKey(parent.location_id, g.levelKey));
      }
      return next;
    });
  }

  private countrySubtreeSelectionFlags(country: TiktokAdGroupLocationOption): { all: boolean; some: boolean } {
    const ids = collectSubtreeLocationIds(country);
    const set = new Set(this.createSparkCampaignService.location_ids());
    let count = 0;
    for (const id of ids) {
      if (set.has(id)) {
        count += 1;
      }
    }
    return {
      all: ids.length > 0 && count === ids.length,
      some: count > 0,
    };
  }

  countryCheckboxChecked(country: TiktokAdGroupLocationOption): boolean {
    return this.countrySubtreeSelectionFlags(country).all;
  }

  countryCheckboxIndeterminate(country: TiktokAdGroupLocationOption): boolean {
    const { all, some } = this.countrySubtreeSelectionFlags(country);
    return some && !all;
  }

  countryBrowseRowClass(country: TiktokAdGroupLocationOption): string {
    const base =
      'flex min-h-11 w-full items-center gap-2 border-b border-gray-100 px-2 py-1.5 transition-colors dark:border-white/10';
    if (this.isCountryDetailActive(country)) {
      return `${base} bg-teal-50/90 dark:bg-teal-950/40`;
    }
    return `${base} bg-white hover:bg-gray-50/80 dark:bg-gray-900/20 dark:hover:bg-white/5`;
  }

  countryLabelClass(country: TiktokAdGroupLocationOption): string {
    const base = 'block min-w-0 flex-1 cursor-pointer truncate text-sm font-medium';
    if (this.isCountryDetailActive(country)) {
      return `${base} text-teal-800 dark:text-teal-200`;
    }
    return `${base} text-gray-900 dark:text-gray-100`;
  }

  /** Lowercase type label on the right (e.g. province, autonomous city). */
  locationLevelHint(level: string | undefined): string {
    const raw = (level ?? '').trim();
    if (!raw) {
      return '';
    }
    return raw
      .toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
  }

  isCountryDetailActive(country: TiktokAdGroupLocationOption): boolean {
    return this.detailCountryNode()?.location_id === country.location_id;
  }

  levelGroupKey(parentId: string, levelKey: string): string {
    return `${parentId}|${levelKey}`;
  }

  isLevelGroupExpanded(parentId: string, levelKey: string): boolean {
    return this.expandedLevelGroupIds().has(this.levelGroupKey(parentId, levelKey));
  }

  toggleLevelGroup(parentId: string, levelKey: string): void {
    const k = this.levelGroupKey(parentId, levelKey);
    this.expandedLevelGroupIds.update((prev) => {
      const next = new Set(prev);
      if (next.has(k)) {
        next.delete(k);
      } else {
        next.add(k);
      }
      return next;
    });
  }

  groupChildrenByLevel(parent: TiktokAdGroupLocationOption): RegionLevelGroup[] {
    const children = parent.sub_regions ?? [];
    if (children.length === 0) {
      return [];
    }
    const bucket = new Map<string, TiktokAdGroupLocationOption[]>();
    for (const c of children) {
      const raw = (c.level ?? '').trim();
      const key = raw ? raw.toUpperCase() : 'OTHER';
      const list = bucket.get(key);
      if (list) {
        list.push(c);
      } else {
        bucket.set(key, [c]);
      }
    }
    const groups: RegionLevelGroup[] = [];
    for (const [levelKey, items] of bucket) {
      groups.push({
        levelKey,
        title: this.humanizeLevelTitle(levelKey),
        items,
      });
    }
    groups.sort((a, b) => {
      const da = levelGroupSortKey(a.levelKey);
      const db = levelGroupSortKey(b.levelKey);
      if (da !== db) {
        return da - db;
      }
      return a.title.localeCompare(b.title);
    });
    return groups;
  }

  humanizeLevelTitle(levelKey: string): string {
    const u = levelKey.toUpperCase();
    const labels: Record<string, string> = {
      PROVINCE: 'Provinces & states',
      STATE: 'States',
      REGION: 'Regions',
      DMA: 'DMAs',
      METROPOLITAN: 'Metropolitan areas',
      METRO: 'Metro areas',
      CITY: 'Cities',
      DISTRICT: 'Districts',
      COUNTY: 'Counties',
      COUNTRY: 'Countries',
      OTHER: 'Other',
    };
    if (labels[u]) {
      return labels[u];
    }
    return levelKey
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');
  }

  groupHeaderPaddingPx(depth: number): number {
    return 8 + depth * 10;
  }

  itemRowPaddingPx(depth: number): number {
    return 14 + depth * 10;
  }

  levelAccordionClass(expanded: boolean): string {
    const base =
      'flex w-full items-center gap-2 rounded-md py-2 pr-2 text-left text-xs font-semibold text-gray-700 dark:text-gray-200';
    if (expanded) {
      return `${base} bg-teal-50/60 dark:bg-teal-950/25`;
    }
    return `${base} hover:bg-gray-50 dark:hover:bg-white/5`;
  }

  isLocationSelected(id: string): boolean {
    return this.createSparkCampaignService.location_ids().includes(id);
  }

  onLocationCheckboxChange(node: TiktokAdGroupLocationOption, ev: Event): void {
    const el = ev.target as HTMLInputElement;
    const checked = el.checked;
    const svc = this.createSparkCampaignService;
    const id = node.location_id;
    if (checked) {
      if (!svc.location_ids().includes(id)) {
        svc.location_ids.set([...svc.location_ids(), id]);
      }
    } else {
      svc.location_ids.set(svc.location_ids().filter((x) => x !== id));
    }
  }

  clearAllLocations(): void {
    this.createSparkCampaignService.location_ids.set([]);
  }

  toggleSelectedLocationsPanel(): void {
    this.selectedLocationsPanelOpen.update((v) => !v);
  }

  locationRowClass(node: TiktokAdGroupLocationOption): string {
    const selected = this.isLocationSelected(node.location_id);
    const base =
      'flex min-h-10 w-full items-center gap-2 rounded-md py-1 pr-2 text-left text-sm text-gray-900 transition-colors dark:text-gray-100';
    if (selected) {
      return `${base} bg-teal-50/80 dark:bg-teal-950/35`;
    }
    return `${base} hover:bg-gray-50 dark:hover:bg-white/5`;
  }

  locationRowPaddingPx(depth: number): number {
    return 8 + depth * 14;
  }

  /** Display label for a region row (data is normalized in CreateSparkCampaign, but this guards empty UI). */
  locationNodeLabel(node: TiktokAdGroupLocationOption): string {
    const label = node.name?.trim();
    if (label) {
      return label;
    }
    return node.location_id ? String(node.location_id) : '—';
  }
}
